import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import TitleHeader from '../common/TitleHeader';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import { getUploadsUrl, vAppAjax } from '@/shared/utils/CommonUtil';
import logger from '@/shared/utils/Logger';
import characterBg from '@/assets/app/images/mypage/chracter_bg.png';
import AppButton from '@/shared/components/ui/new/AppButton';
import { useNavigate } from 'react-router';

interface Character {
  characterId: number;
  characterName: string;
  provisionTypeCode: string;
  imageFilename: string | null;
  originalFilename: string | null;
  description: string | null;
  price: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  thumbnailFilename: string | null;
  thumbnailOrgFilename: string | null;
  descImageFilename: string | null;
  descOrgFilename: string | null;
}

interface CharacterSelectRequest {
  characterId: number;
}

const ITEM_WIDTH = 80; // 아이템 너비
const GAP = 16; // 아이템 간격
const STEP = ITEM_WIDTH + GAP;

// 캐릭터 이미지 원본 1px이 카드 폭의 몇 %인지.
// 업로드된 캐릭터 이미지와 소개 이미지(폭 2458px)는 같은 배율로 그려져 있어, 시안 캐릭터 시트에서
// 캐릭터 키 : 소개 상자 폭 비율이 원본 픽셀 비율과 같다(2026-11-01 측정, 5종 모두 오차 1% 이내).
// 소개 이미지가 카드 폭에 맞춰 그려지므로 같은 배율이면 100 / 2458인데, 그대로면 루나 귀가 카드 위 끝에 닿아 0.8배로 줄였다.
const CHAR_SCALE = (100 / 2458) * 0.8;

const CharacterChange: React.FC = () => {
  const { member, checkAttendance } = useAppAuthStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(2);
  // 지금 보이는 캐릭터 이미지의 원본 폭 (다른 캐릭터로 바뀌는 순간 이전 값으로 그려지지 않게 src도 함께 저장)
  const [charSize, setCharSize] = useState<{ src: string; width: number }>();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<number | undefined>(
    member?.selectedCharacterId || 14,
  );

  // 캐릭터 목록 조회
  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    setLoading(true);
    await vAppAjax(
      'get',
      `characters`,
      {},
      (response: AxiosResponse<CommonResult<Character[]>>) => {
        const resultData = response.data;
        setLoading(false);
        if (resultData.code === 200) {
          setCharacters(resultData.result);
          resultData.result.forEach((item, index) => {
            if (item.characterId === member?.selectedCharacterId) {
              setSelectedIndex(index);
            }
          });
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  const handleSelectCharacter = async () => {
    const selectedId = characters[selectedIndex].characterId;
    if (member?.selectedCharacterId === selectedId) {
      navigate('/mypage');
      return;
    }

    await vAppAjax(
      'post',
      `characters/select`,
      { characterId: selectedId },
      (response: AxiosResponse<CommonResult<CharacterSelectRequest>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          checkAttendance();
          navigate('/mypage');
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  // 드래그가 끝났을 때 어느 칸에 멈출지 계산하는 함수
  const handleDragEnd = (_: any, info: any) => {
    const dragOffset = info.offset.x;
    const dragThreshold = 50; // 50px 이상 밀었을 때 다음 칸으로 이동

    if (dragOffset < -dragThreshold && selectedIndex < characters.length - 1) {
      setSelectedIndex(prev => prev + 1);
    } else if (dragOffset > dragThreshold && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return <></>;
  }

  const charSrc = `${getUploadsUrl()}/character/${characters[selectedIndex].imageFilename}`;

  return (
    <div className="flex flex-col bg-white" style={{ minHeight: '100vh' }}>
      <TitleHeader title="캐릭터변경" onBack={() => navigate(-1)} />

      <main className="flex-1 px-4 items-start justify-center" style={{ marginTop: 5 }}>
        <div
          className="w-full relative rounded-[20px] overflow-hidden"
          style={{
            backgroundImage: `url(${characterBg})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '310 / 448',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={characters[selectedIndex].characterId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute inset-0"
            >
              {/* 캐릭터 이미지는 원본 픽셀 크기에 비례해 그린다(높이 고정이면 작은 톰토미와 귀가 긴 루나가 같은 키로 보임).
                  이미지 아래 끝은 소개 상자 바로 위(카드 높이의 35.8%)에 둔다. */}
              <img
                src={charSrc}
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                  bottom: '35.8%',
                  width: charSize?.src === charSrc ? `${charSize.width * CHAR_SCALE}%` : undefined,
                  visibility: charSize?.src === charSrc ? 'visible' : 'hidden',
                }}
                onLoad={e =>
                  setCharSize({ src: charSrc, width: e.currentTarget.naturalWidth })
                }
                alt="char"
              />
            </motion.div>
            <div className="text-center absolute" style={{ bottom: '4%' }}>
              {characters[selectedIndex].descImageFilename && (
                <img
                  src={`${getUploadsUrl()}/character/${characters[selectedIndex].descImageFilename}`}
                />
              )}
            </div>
          </AnimatePresence>
        </div>

        <div className="w-full flex justify-center items-center mt-3 mb-5">
          {/* 전체 배경 베이스 트랙 (연한 블루) */}
          <div className="w-100 h-2 bg-[#E2F3FF] rounded-[20px] relative overflow-hidden">
            {/* 현재 선택된 위치를 나타내는 활성화 바 (진한 블루) */}
            <motion.div
              className="h-full bg-[#38B6FF] rounded-[20px] absolute top-0 left-0"
              style={{
                // 캐릭터 전체 개수에 맞춰 활성화 바의 너비를 균등 분할
                width: `${100 / (characters.length || 1)}%`,
              }}
              animate={{
                // 현재 선택된 인덱스만큼 x축으로 정밀 이동
                x: `${selectedIndex * 100}%`,
              }}
              transition={{ type: 'spring', stiffness: 250, damping: 28 }}
            />
          </div>
        </div>

        {/* 하단 슬라이더 영역 */}
        <div className="w-full overflow-visible flex items-center justify-center relative mt-4 h-32">
          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: 0 }} // 한 칸씩 제어하기 위해 축 고정
            onDragEnd={handleDragEnd}
            animate={{
              // 핵심: 현재 인덱스만큼 왼쪽으로 밀기
              x: -((selectedIndex - 2) * STEP),
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="flex items-center cursor-grab active:cursor-grabbing"
            style={{
              // 트랙을 중앙에 배치하기 위해 width를 0으로 만들거나 최소화
              width: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {characters.map((char, idx) => {
              const isActive = idx === selectedIndex;
              return (
                <motion.div
                  key={char.characterId}
                  onClick={() => setSelectedIndex(idx)}
                  animate={{
                    scale: isActive ? 1.15 : 1.05,
                    opacity: isActive ? 1 : 0.6,
                  }}
                  className={`relative w-[100px] h-[100px] shrink-0 rounded-full flex items-center justify-center`}
                  style={{
                    // 중앙 배치를 위해 양옆 마진을 동일하게 부여 (STEP 유지)
                    marginLeft: GAP / 2,
                    marginRight: GAP / 2,
                    transformOrigin: 'bottom center',
                  }}
                >
                  <img
                    src={`${getUploadsUrl()}/character/${characters[idx].thumbnailFilename}`}
                    className="rounded-full w-full h-full object-contain p-1"
                  />
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-0 left-0 bg-[#53C6FF] p-1 rounded-full border-2 border-white z-20"
                    >
                      <Check className="w-4 h-4 text-white stroke-[z]" />
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        <AppButton
          onConfirm={handleSelectCharacter}
          style={{ marginTop: 10, marginBottom: 30, fontSize: 28 }}
          className="f-one-pop"
          message={'적용하기'}
        />
      </main>
    </div>
  );
};

export default CharacterChange;
